import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

interface Props {
  url: string;
  duration: number;
  start: number;
  end: number;
  onChange: (start: number, end: number) => void;
}

const THUMB_COUNT = 12;

const fmt = (s: number) => {
  const sec = Math.max(0, s);
  const m = Math.floor(sec / 60);
  const r = Math.floor(sec % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
};

/**
 * Cắt video kiểu Facebook Reels: dải phim (thumbnail) + 2 mép kéo thả chọn đoạn giữ + playhead.
 * Chỉ đổi start/end (giây) qua onChange — không đụng backend. Clip là file local (blob url) nên
 * canvas không bị taint CORS khi bắt thumbnail.
 */
export const VideoTrimmer = ({ url, duration, start, end, onChange }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<null | 'start' | 'end' | 'seek'>(null);
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [playing, setPlaying] = useState(false);
  const [cursor, setCursor] = useState(start); // vị trí playhead (giây)

  // Bắt thumbnail cho dải phim bằng cách tua 1 video ẩn.
  useEffect(() => {
    let cancelled = false;
    const v = document.createElement('video');
    v.src = url;
    v.muted = true;
    const canvas = document.createElement('canvas');
    const out: string[] = [];
    const gen = async () => {
      await new Promise<void>((res) => {
        v.onloadeddata = () => res();
        v.onerror = () => res();
      });
      if (cancelled || !v.videoWidth) return;
      canvas.width = 90;
      canvas.height = Math.max(1, Math.round((90 * v.videoHeight) / v.videoWidth));
      const ctx = canvas.getContext('2d');
      for (let i = 0; i < THUMB_COUNT; i++) {
        if (cancelled) return;
        const t = Math.min((duration * i) / THUMB_COUNT + 0.01, Math.max(0, duration - 0.05));
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((res) => {
          v.onseeked = () => res();
          v.currentTime = t;
        });
        if (cancelled) return;
        try {
          ctx?.drawImage(v, 0, 0, canvas.width, canvas.height);
          out.push(canvas.toDataURL('image/jpeg', 0.5));
          setThumbs([...out]);
        } catch {
          /* bỏ qua frame lỗi */
        }
      }
    };
    gen();
    return () => {
      cancelled = true;
    };
  }, [url, duration]);

  const pct = (t: number) => (duration > 0 ? `${(t / duration) * 100}%` : '0%');
  const rightPct = (t: number) => (duration > 0 ? `${100 - (t / duration) * 100}%` : '100%');

  const seek = useCallback((t: number) => {
    setCursor(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  }, []);

  const timeFromX = useCallback(
    (clientX: number) => {
      const el = stripRef.current;
      if (!el) return 0;
      const r = el.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      return ratio * duration;
    },
    [duration]
  );

  const applyMove = useCallback(
    (clientX: number) => {
      const t = timeFromX(clientX);
      const which = dragRef.current;
      if (which === 'start') {
        const ns = Math.max(0, Math.min(t, end - 0.1));
        onChange(ns, end);
        seek(ns);
      } else if (which === 'end') {
        const ne = Math.min(duration, Math.max(t, start + 0.1));
        onChange(start, ne);
        seek(ne);
      } else if (which === 'seek') {
        seek(Math.min(end, Math.max(start, t)));
      }
    },
    [timeFromX, end, start, duration, onChange, seek]
  );

  const onDown = (which: 'start' | 'end' | 'seek') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = which;
    stripRef.current?.setPointerCapture?.(e.pointerId);
    applyMove(e.clientX);
  };
  const onMove = (e: React.PointerEvent) => {
    if (dragRef.current) applyMove(e.clientX);
  };
  const onUp = () => {
    dragRef.current = null;
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
      setPlaying(false);
    } else {
      if (v.currentTime < start || v.currentTime >= end) v.currentTime = start;
      v.play();
      setPlaying(true);
    }
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime >= end) {
      v.pause();
      v.currentTime = start;
      setPlaying(false);
      setCursor(start);
    } else {
      setCursor(v.currentTime);
    }
  };

  return (
    <div className="select-none">
      <div
        className="bg-black rounded-lg overflow-hidden flex items-center justify-center"
        style={{ aspectRatio: '9 / 16', maxHeight: 360 }}
      >
        <video
          ref={videoRef}
          src={url}
          className="max-h-full max-w-full"
          onTimeUpdate={onTimeUpdate}
          onClick={togglePlay}
          playsInline
        />
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button onClick={togglePlay} className="ui-text shrink-0" title={playing ? 'Tạm dừng' : 'Phát đoạn đã chọn'}>
          {playing ? <Pause size={20} /> : <Play size={20} />}
        </button>

        <div
          ref={stripRef}
          className="relative flex-1 h-14 rounded-md overflow-hidden bg-black cursor-pointer touch-none"
          onPointerDown={onDown('seek')}
          onPointerMove={onMove}
          onPointerUp={onUp}
        >
          <div className="absolute inset-0 flex">
            {thumbs.length === 0
              ? null
              : thumbs.map((s, i) => (
                  <img key={i} src={s} alt="" draggable={false} className="h-full flex-1 object-cover" />
                ))}
          </div>
          {/* làm mờ phần bị cắt bỏ */}
          <div className="absolute inset-y-0 left-0 bg-black/60 pointer-events-none" style={{ width: pct(start) }} />
          <div className="absolute inset-y-0 right-0 bg-black/60 pointer-events-none" style={{ width: rightPct(end) }} />
          {/* viền đoạn giữ */}
          <div
            className="absolute inset-y-0 border-2 border-sky-400 pointer-events-none"
            style={{ left: pct(start), right: rightPct(end) }}
          />
          {/* mép trái */}
          <div
            className="absolute inset-y-0 w-3 bg-sky-400 rounded-l cursor-ew-resize flex items-center justify-center"
            style={{ left: pct(start) }}
            onPointerDown={onDown('start')}
            onPointerMove={onMove}
            onPointerUp={onUp}
          >
            <div className="w-0.5 h-5 bg-white/90" />
          </div>
          {/* mép phải */}
          <div
            className="absolute inset-y-0 w-3 bg-sky-400 rounded-r cursor-ew-resize flex items-center justify-center -translate-x-full"
            style={{ left: pct(end) }}
            onPointerDown={onDown('end')}
            onPointerMove={onMove}
            onPointerUp={onUp}
          >
            <div className="w-0.5 h-5 bg-white/90" />
          </div>
          {/* playhead */}
          <div className="absolute inset-y-0 w-0.5 bg-white pointer-events-none" style={{ left: pct(cursor) }} />
        </div>

        <span className="text-xs ui-muted tabular-nums whitespace-nowrap shrink-0">{fmt(end - start)}</span>
      </div>

      <p className="text-xs ui-muted mt-2">
        Kéo 2 mép xanh để chọn đoạn giữ · Đang chọn: {start.toFixed(1)}s → {end.toFixed(1)}s
      </p>
    </div>
  );
};
